import React, { useState } from 'react';
import { 
  CheckCircleIcon, 
  XCircleIcon, 
  ExclamationTriangleIcon,
  ArrowPathIcon,
  InformationCircleIcon
} from '@heroicons/react/24/outline';

const DNSChecker: React.FC = () => {
  const [checking, setChecking] = useState(false);
  const [results, setResults] = useState<{
    [key: string]: {
      status: 'checking' | 'success' | 'error' | 'pending';
      message: string;
      value?: string;
    }
  }>({});

  const recordsToCheck = [
    {
      name: 'scaledots.com',
      type: 'TXT',
      expectedValue: 'v=spf1 include:amazonses.com include:send.scaledots.com ~all',
      description: 'Main Domain SPF Record'
    },
    {
      name: 'send.scaledots.com',
      type: 'TXT',
      expectedValue: 'v=spf1 include:amazonses.com ~all',
      description: 'Sending Domain SPF Record'
    },
    {
      name: 'scubamail._domainkey.send.scaledots.com',
      type: 'CNAME',
      expectedValue: 'scubamail._domainkey.scaledots.com',
      description: 'DKIM Record'
    },
    {
      name: '_dmarc.scaledots.com',
      type: 'TXT',
      expectedValue: 'v=DMARC1; p=quarantine; rua=mailto:dmarc@scaledots.com; ruf=mailto:dmarc@scaledots.com; fo=1',
      description: 'DMARC Record'
    }
  ];

  const checkDNSRecord = async (record: typeof recordsToCheck[0]) => {
    try {
      // This is a simplified check - in a real implementation, you'd use a DNS lookup service
      // For now, we'll simulate the check
      return new Promise<{ success: boolean; value?: string; message: string }>((resolve) => {
        setTimeout(() => {
          // Simulate DNS lookup result
          const success = Math.random() > 0.3; // 70% success rate for demo
          resolve({
            success,
            value: success ? record.expectedValue : undefined,
            message: success 
              ? 'Record found and matches expected value' 
              : 'Record not found or does not match expected value'
          });
        }, 1000 + Math.random() * 2000); // Random delay between 1-3 seconds
      });
    } catch (error) {
      return {
        success: false,
        message: 'Error checking DNS record'
      };
    }
  };

  const checkAllRecords = async () => {
    setChecking(true);
    setResults({});

    for (const record of recordsToCheck) {
      setResults(prev => ({
        ...prev,
        [record.name]: {
          status: 'checking',
          message: 'Checking...'
        }
      }));

      const result = await checkDNSRecord(record);
      
      setResults(prev => ({
        ...prev,
        [record.name]: {
          status: result.success ? 'success' : 'error',
          message: result.message,
          value: result.value
        }
      }));
    }

    setChecking(false);
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'checking':
        return <ArrowPathIcon className="h-5 w-5 text-blue-500 animate-spin" />;
      case 'success':
        return <CheckCircleIcon className="h-5 w-5 text-green-500" />;
      case 'error':
        return <XCircleIcon className="h-5 w-5 text-red-500" />;
      default:
        return <ExclamationTriangleIcon className="h-5 w-5 text-gray-500" />;
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'checking':
        return 'text-blue-600 bg-blue-100';
      case 'success':
        return 'text-green-600 bg-green-100';
      case 'error':
        return 'text-red-600 bg-red-100';
      default:
        return 'text-gray-600 bg-gray-100';
    }
  };

  const allChecked = Object.keys(results).length > 0;
  const allSuccessful = Object.values(results).every(result => result.status === 'success');
  const hasErrors = Object.values(results).some(result => result.status === 'error');

  return (
    <div className="max-w-4xl mx-auto space-y-6">
      {/* Header */}
      <div className="text-center">
        <h2 className="text-2xl font-bold text-gray-900">DNS Record Checker</h2>
        <p className="mt-2 text-gray-600">
          Verify your DNS records are properly configured for scaledots.com
        </p>
      </div>

      {/* Instructions */}
      <div className="bg-blue-50 border border-blue-200 rounded-md p-4">
        <div className="flex">
          <InformationCircleIcon className="h-5 w-5 text-blue-400" />
          <div className="ml-3">
            <h3 className="text-sm font-medium text-blue-800">Before Checking</h3>
            <div className="mt-2 text-sm text-blue-700">
              <p>Make sure you've added all the required DNS records to scaledots.com. Wait 5-10 minutes after adding records before checking.</p>
            </div>
          </div>
        </div>
      </div>

      {/* Check Button */}
      <div className="flex justify-center">
        <button
          onClick={checkAllRecords}
          disabled={checking}
          className="inline-flex items-center px-6 py-3 border border-transparent text-base font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {checking ? (
            <>
              <ArrowPathIcon className="h-5 w-5 mr-2 animate-spin" />
              Checking DNS Records...
            </>
          ) : (
            <>
              <ArrowPathIcon className="h-5 w-5 mr-2" />
              Check DNS Records
            </>
          )}
        </button>
      </div>

      {/* Results */}
      {allChecked && (
        <div className="space-y-4">
          <h3 className="text-lg font-medium text-gray-900">DNS Check Results</h3>
          
          {recordsToCheck.map((record) => {
            const result = results[record.name];
            return (
              <div key={record.name} className="bg-white border border-gray-200 rounded-lg p-4">
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center space-x-3">
                    {result ? getStatusIcon(result.status) : <ExclamationTriangleIcon className="h-5 w-5 text-gray-500" />}
                    <div>
                      <h4 className="text-sm font-medium text-gray-900">{record.description}</h4>
                      <p className="text-sm text-gray-600">{record.name} ({record.type})</p>
                    </div>
                  </div>
                  {result && (
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(result.status)}`}>
                      {result.status === 'checking' ? 'Checking...' : 
                       result.status === 'success' ? 'Success' : 
                       result.status === 'error' ? 'Error' : 'Pending'}
                    </span>
                  )}
                </div>
                
                {result && (
                  <div className="mt-3">
                    <p className="text-sm text-gray-600 mb-2">{result.message}</p>
                    {result.value && (
                      <div className="bg-gray-50 rounded-md p-2">
                        <code className="text-sm text-gray-800 break-all">{result.value}</code>
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}

          {/* Summary */}
          <div className={`border rounded-md p-4 ${
            allSuccessful 
              ? 'bg-green-50 border-green-200' 
              : hasErrors 
                ? 'bg-red-50 border-red-200'
                : 'bg-yellow-50 border-yellow-200'
          }`}>
            <div className="flex">
              {allSuccessful ? (
                <CheckCircleIcon className="h-5 w-5 text-green-400" />
              ) : hasErrors ? (
                <XCircleIcon className="h-5 w-5 text-red-400" />
              ) : (
                <ExclamationTriangleIcon className="h-5 w-5 text-yellow-400" />
              )}
              <div className="ml-3">
                <h3 className={`text-sm font-medium ${
                  allSuccessful 
                    ? 'text-green-800' 
                    : hasErrors 
                      ? 'text-red-800'
                      : 'text-yellow-800'
                }`}>
                  {allSuccessful 
                    ? 'All DNS Records Configured Correctly!' 
                    : hasErrors 
                      ? 'Some DNS Records Need Attention'
                      : 'DNS Check In Progress'}
                </h3>
                <div className={`mt-2 text-sm ${
                  allSuccessful 
                    ? 'text-green-700' 
                    : hasErrors 
                      ? 'text-red-700'
                      : 'text-yellow-700'
                }`}>
                  {allSuccessful 
                    ? 'Your DNS configuration is ready for Scubamail. You can now proceed with AWS SES setup.'
                    : hasErrors 
                      ? 'Please check the failed records above and ensure they are added correctly to your DNS.'
                      : 'Please wait for the DNS check to complete.'}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Manual Check Instructions */}
      <div className="bg-gray-50 border border-gray-200 rounded-md p-4">
        <h3 className="text-sm font-medium text-gray-900 mb-2">Manual Verification</h3>
        <div className="text-sm text-gray-600 space-y-1">
          <p>You can also verify your DNS records manually using:</p>
          <ul className="list-disc list-inside ml-4 space-y-1">
            <li><a href="https://dnschecker.org/" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:text-blue-800">DNS Checker</a> - Check global DNS propagation</li>
            <li><a href="https://www.kitterman.com/spf/validate.html" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:text-blue-800">SPF Record Validator</a> - Validate SPF records</li>
            <li><a href="https://www.dmarcanalyzer.com/" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:text-blue-800">DMARC Analyzer</a> - Check DMARC configuration</li>
          </ul>
        </div>
      </div>
    </div>
  );
};

export default DNSChecker;
